import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Check, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { SEO_META, SITE, waUrl, WA_GENERAL } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", phone: "", country: "", message: "" };

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="field-label">{label}{required && <span className="text-cleef"> *</span>}</span>
    {children}
  </label>
);

export default function Contact() {
  const meta = SEO_META.contact;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads/contact`, form);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "contact_form_submit" });
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="border-b border-obsidian/10 bg-bone" data-testid="contact-hero">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <p className="eyebrow">Contact</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">
              Let’s discuss your uniform requirement.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="container-site grid gap-12 py-16 lg:grid-cols-12 lg:py-24" data-testid="contact-section">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="space-y-px border border-obsidian/10 bg-obsidian/10">
              <a href={`mailto:${SITE.email}`} data-event="email_click" data-testid="contact-email-card" className="group flex items-start gap-4 bg-white p-6 transition-colors hover:bg-ivory">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-cleef" />
                <span>
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-obsidian/45">Email</span>
                  <span className="mt-1 block font-display text-lg font-bold group-hover:text-cleef">{SITE.email}</span>
                </span>
              </a>
              <a href={waUrl(WA_GENERAL)} target="_blank" rel="noopener noreferrer" data-event="whatsapp_click" data-testid="contact-whatsapp-card" className="group flex items-start gap-4 bg-white p-6 transition-colors hover:bg-ivory">
                <WhatsAppIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#25D366]" />
                <span>
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-obsidian/45">WhatsApp</span>
                  <span className="mt-1 block font-display text-lg font-bold group-hover:text-[#128C7E]">{SITE.whatsappDisplay}</span>
                </span>
              </a>
              <div className="flex items-start gap-4 bg-white p-6">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-cleef" />
                <span>
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-obsidian/45">Business Location</span>
                  <span className="mt-1 block font-display text-lg font-bold">{SITE.location}</span>
                </span>
              </div>
              <div className="flex items-start gap-4 bg-white p-6">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-cleef" />
                <span>
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-obsidian/45">Business Hours</span>
                  <span className="mt-1 block font-display text-lg font-bold">{SITE.hours}</span>
                </span>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-obsidian/55">
              For detailed quotations with quantities, branding and delivery requirements, use the{" "}
              <a href="/bulk-quote" className="font-semibold text-cleef underline underline-offset-4">bulk quotation form</a>.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <div className="border border-obsidian/10 bg-white p-10 text-center" data-testid="contact-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center bg-cleef-light">
                  <Check className="h-7 w-7 text-cleef" />
                </span>
                <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight">Thank you.</h2>
                <p className="mt-3 text-base text-obsidian/60">Your enquiry has been received. Our team will review the details and contact you.</p>
                <a href={waUrl(WA_GENERAL)} target="_blank" rel="noopener noreferrer" data-event="whatsapp_click" data-testid="contact-success-whatsapp-btn" className="btn-dark mt-7 !bg-[#128C7E]">
                  <WhatsAppIcon className="h-4 w-4" /> Continue on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-obsidian/10 bg-white p-7 sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Full Name" required><input required className="field-input" value={form.name} onChange={set("name")} data-testid="contact-input-name" autoComplete="name" /></Field>
                  <Field label="Company Name"><input className="field-input" value={form.company} onChange={set("company")} data-testid="contact-input-company" autoComplete="organization" /></Field>
                  <Field label="Business Email" required><input required type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="contact-input-email" autoComplete="email" /></Field>
                  <Field label="Phone / WhatsApp"><input type="tel" className="field-input" value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" autoComplete="tel" /></Field>
                  <Field label="Country"><input className="field-input sm:col-span-2" value={form.country} onChange={set("country")} data-testid="contact-input-country" autoComplete="country-name" /></Field>
                  <Field label="Message" required><textarea required rows={5} className="field-input resize-none sm:col-span-2" placeholder="Tell us about your uniform requirement." value={form.message} onChange={set("message")} data-testid="contact-input-message" /></Field>
                </div>
                <button type="submit" disabled={sending} data-event="contact_form_submit" data-testid="contact-submit-button" className="btn-primary mt-7 w-full disabled:opacity-60 sm:w-auto">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send Enquiry
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
